import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, Center, Icon, Spinner } from '@barev/ui';
import { confirmDialog, errorText, haptic, paramsExtras, useI18n, useToast, useTRPC } from '@barev/web';
import { useRef, useState } from 'react';
import { authHeader } from '../auth.ts';
import { PageHead, useJobSummary } from '../lib.tsx';

/**
 * PRO-3/PRO-4: заказ по шагам. Кнопки берутся из state machine (бэкенд их же и проверяет),
 * закрытие недоступно, пока не отмечен весь чек-лист и не приложено фото.
 */
export function Job({ id }: { id: string }) {
  const { t, amd, dt } = useI18n();
  const trpc = useTRPC();
  const qc = useQueryClient();
  const toast = useToast();
  const summary = useJobSummary();
  const job = useQuery({ ...trpc.pro.order.queryOptions({ id }), refetchInterval: 20_000 });
  const refresh = () => qc.invalidateQueries();
  const action = useMutation(
    trpc.pro.action.mutationOptions({
      onSuccess: () => {
        haptic.ok();
        void refresh();
      },
      onError: (e) => {
        haptic.error();
        toast(errorText(t, e), 'error');
        void refresh();
      },
    }),
  );
  const check = useMutation(trpc.pro.checklist.mutationOptions({ onSuccess: refresh, onError: (e) => toast(errorText(t, e), 'error') }));
  const paid = useMutation(
    trpc.pro.markPaid.mutationOptions({
      onSuccess: () => {
        toast(t('pro.job.paidMarked'));
        void refresh();
      },
      onError: (e) => toast(errorText(t, e), 'error'),
    }),
  );

  if (!job.data)
    return (
      <Center>
        <Spinner />
      </Center>
    );
  const j = job.data;
  const checklistDone = j.checklist.every((c) => c.checked && (!c.requiresPhoto || c.hasPhoto));
  const finished = j.actions.length === 0 && ['DONE', 'COMPLETED', 'PLAN_SENT'].includes(j.status);

  return (
    <main className="app app--plain">
      <PageHead title={`#${j.number}`} />
      <Card tone="home" className="bc-stack">
        <div className="bc-between" style={{ alignItems: 'flex-start' }}>
          <h2 className="bc-h2">{summary(j)}</h2>
          <span className="bc-badge">{t(`status.${j.flow}.${j.status}`)}</span>
        </div>
        <dl className="kv" style={{ margin: 0 }}>
          <dt>{t('pro.job.when')}</dt>
          <dd>
            {dt(j.scheduledAt, { weekday: 'short', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' })} · {t('notify.duration', { minutes: j.durationMinutes })}
          </dd>
          <dt>{t('pro.job.where')}</dt>
          <dd>
            {j.isOnline ? (
              t('common.online')
            ) : (
              <a href={`https://yandex.ru/maps/?text=${encodeURIComponent(`Ереван, ${j.addressText ?? ''}`)}`} target="_blank" rel="noreferrer">
                {j.districtCode ? `${t(`districts.${j.districtCode}`)}, ` : ''}
                {j.addressText}
              </a>
            )}
          </dd>
          <dt>{t('pro.job.client')}</dt>
          <dd>
            {j.clientName ?? '—'} · {t('pro.job.clientLanguage')}: {j.clientLocale.toUpperCase()}
          </dd>
          <dt>{t('pro.job.price')}</dt>
          <dd>
            {amd(j.dueAmount)} · {t(`payment.${j.paymentStatus}`)}
          </dd>
        </dl>
        {paramsExtras(t, j.params).length > 0 && (
          <div className="bc-card bc-card--tight" style={{ background: 'rgba(255,255,255,.6)' }}>
            <div className="bc-small" style={{ fontWeight: 700, marginBottom: 4 }}>
              {t('client.order.extras')}
            </div>
            {paramsExtras(t, j.params).map((e) => (
              <div key={e.code}>• {e.label}</div>
            ))}
          </div>
        )}
        {j.note && (
          <div className="bc-card bc-card--tight" style={{ background: 'rgba(255,255,255,.6)' }}>
            💬 {j.note}
          </div>
        )}
      </Card>

      {j.checklist.length > 0 && (j.checklistActive || finished) && (
        <Card className="bc-stack">
          <h2 className="bc-h3">{t('pro.job.checklist')}</h2>
          {j.checklist.map((c) =>
            c.requiresPhoto ? (
              <PhotoItem key={c.code} orderId={j.id} code={c.code} done={c.checked && c.hasPhoto} disabled={!j.checklistActive} onDone={refresh} />
            ) : (
              <label key={c.code} className={`bc-check ${c.checked ? 'bc-check--done' : ''}`}>
                <input type="checkbox" checked={c.checked} disabled={!j.checklistActive || check.isPending} onChange={(e) => check.mutate({ orderId: j.id, code: c.code, checked: e.target.checked })} />
                <span style={{ fontWeight: 600 }}>{t(`checklist.${c.code}`)}</span>
              </label>
            ),
          )}
        </Card>
      )}

      <div className="bc-stack" style={{ gap: 8 }}>
        {j.actions.map((a) => {
          const blocked = a.guards.includes('checklistComplete') && !checklistDone;
          return (
            <Button
              key={a.code}
              block
              variant={a.kind === 'event' ? 'light' : 'dark'}
              disabled={action.isPending || blocked}
              onClick={async () => {
                if (a.to && ['DONE', 'COMPLETED'].includes(a.to) && !(await confirmDialog(t('pro.job.confirmFinish')))) return;
                action.mutate({ orderId: j.id, action: a.code });
              }}
            >
              {t(`pro.action.${a.code}`)}
            </Button>
          );
        })}
        {j.actions.some((a) => a.guards.includes('checklistComplete')) && !checklistDone && (
          <p className="bc-small bc-muted" style={{ margin: 0, textAlign: 'center' }}>
            {t('pro.job.checklistFirst')}
          </p>
        )}
        {j.paymentStatus === 'UNPAID' && j.status !== 'NEW' && (
          <Button variant="outline" block disabled={paid.isPending} onClick={async () => (await confirmDialog(t('pro.job.confirmPaid', { amount: amd(j.dueAmount) }))) && paid.mutate({ orderId: j.id })}>
            {t('pro.job.markPaid')}
          </Button>
        )}
      </div>
    </main>
  );
}

/** Пункт чек-листа с обязательным фото: камера/галерея → загрузка → пункт отмечен */
function PhotoItem({ orderId, code, done, disabled, onDone }: { orderId: string; code: string; done: boolean; disabled: boolean; onDone: () => void }) {
  const { t } = useI18n();
  const toast = useToast();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  async function upload(file: File) {
    setBusy(true);
    try {
      const body = new FormData();
      body.set('orderId', orderId);
      body.set('code', code);
      body.set('file', await shrink(file));
      const res = await fetch('/files/pro/checklist-photo', { method: 'POST', headers: authHeader(), body });
      if (!res.ok) {
        const err = (await res.json().catch(() => ({}))) as { error?: string };
        throw Object.assign(new Error(err.error ?? 'UPLOAD'), { data: { domainCode: err.error } });
      }
      haptic.ok();
      onDone();
    } catch (e) {
      toast(errorText(t, e), 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={`bc-check ${done ? 'bc-check--done' : ''}`} style={{ cursor: 'default' }}>
      <Icon name={done ? 'check' : 'camera'} style={{ color: done ? 'var(--bc-ok)' : undefined }} />
      <span style={{ flex: 1, fontWeight: 600 }}>{t(`checklist.${code}`)}</span>
      <input ref={input} type="file" accept="image/*" capture="environment" hidden onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
      <Button size="sm" variant={done ? 'light' : 'dark'} disabled={disabled || busy} onClick={() => input.current?.click()}>
        {busy ? t('common.loading') : done ? t('pro.job.retakePhoto') : t('pro.job.takePhoto')}
      </Button>
    </div>
  );
}

/** Уменьшаем фото до 1600 px — быстрее на мобильном интернете */
async function shrink(file: File): Promise<Blob> {
  if (!file.type.startsWith('image/') || file.type === 'image/heic') return file;
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, 1600 / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    canvas.getContext('2d')!.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    return await new Promise<Blob>((resolve) => canvas.toBlob((b) => resolve(b ?? file), 'image/jpeg', 0.85));
  } catch {
    return file;
  }
}
